import { RefObject, useEffect, useRef, useState } from "react";

/**
 * `driven`: the walkthrough's illustration is the clock (it plays at its own
 * pace and reports the step it is at), so there is no timer; `running` says
 * whether it should be playing (not paused, on screen, not held).
 */
const useDemoPlayback = (lastStage: number, driven = false) => {
  const reducedMotion = window.matchMedia(
    "(prefers-reduced-motion: reduce)"
  ).matches;
  // Reduced motion: no autoplay. The driven walkthrough rests on its final
  // frame; the timer-driven playback keeps resting on its first event.
  const [stage, setStage] = useState(
    reducedMotion ? (driven ? lastStage : 1) : driven ? 1 : 0
  );
  const [playing, setPlaying] = useState(!reducedMotion);
  const chartRef = useRef<HTMLDivElement>(null);
  const visible = usePlaybackVisibility(chartRef);
  // No pause on hover: it was easy to trigger by accident and read as a freeze.
  const held = false;
  useEffect(() => {
    if (driven || !visible || !playing || held) return;
    const timer = setTimeout(
      () => setStage((current) => (current === lastStage ? 0 : current + 1)),
      stage === 0 ? 650 : 2000
    );
    return () => clearTimeout(timer);
  }, [driven, visible, playing, held, stage, lastStage]);
  const running = playing && visible && !held;
  const advance = (): void => {
    if (driven) {
      // The next step's frame, held; after the last, play again from the start.
      const replay = stage === lastStage;
      setPlaying(replay && !reducedMotion);
      setStage(replay ? 1 : stage + 1);
      return;
    }
    setPlaying(stage === lastStage && !reducedMotion);
    setStage(stage === lastStage ? (reducedMotion ? 1 : 0) : stage + 1);
  };
  const togglePlayback = (): void => setPlaying((current) => !current);
  // Jumps to an event. A driven illustration plays on from there; the
  // timer-driven example holds.
  const goTo = (next: number): void => {
    setPlaying(driven && !reducedMotion);
    setStage(next);
  };
  return {
    stage,
    setStage,
    chartRef,
    advance,
    playing,
    running,
    held,
    togglePlayback,
    goTo,
  };
};

export default useDemoPlayback;

const usePlaybackVisibility = (
  chartRef: RefObject<HTMLDivElement | null>
): boolean => {
  const [intersecting, setIntersecting] = useState(false);
  const [foreground, setForeground] = useState(!document.hidden);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => setIntersecting(entry.intersectionRatio >= 0.35),
      { threshold: 0.35 }
    );
    if (chartRef.current) observer.observe(chartRef.current);
    const onVisibility = (): void => setForeground(!document.hidden);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      observer.disconnect();
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [chartRef]);
  return intersecting && foreground;
};
